import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import { useAgencyBookings, useAgencyMembers } from "@/hooks/platform/useAgency";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const statusStyle: Record<string, string> = {
  pending: "bg-amber-600",
  confirmed: "bg-emerald-600",
  cancelled: "bg-red-600",
  completed: "bg-blue-600",
};

const BookingManager = () => {
  const { data: bookings = [], isLoading, error, refetch } = useAgencyBookings();
  const { data: members = [] } = useAgencyMembers();
  const nameOf = (id: string) => {
    const m = members.find((x) => x.id === id);
    return m?.business_name || m?.full_name || m?.username || "Unknown";
  };

  if (isLoading) return <LoadingState label="Loading bookings…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (bookings.length === 0)
    return <EmptyState icon={Calendar} title="No bookings yet" description="Creator bookings across the roster appear here." />;

  return (
    <div className="space-y-3">
      {bookings.map((b) => (
        <Card key={b.id} className="bg-white/5 border-white/20">
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white">{b.title || "Booking"}</p>
                <Badge className={`${statusStyle[b.status] ?? "bg-gray-700"} border-0 capitalize`}>{b.status}</Badge>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {nameOf(b.creator_id)} · {nameOf(b.venue_id)} · {format(new Date(b.event_date), "d MMM yyyy HH:mm")}
              </p>
              {b.notes && <p className="text-xs text-gray-500 mt-1">{b.notes}</p>}
            </div>
            <p className="text-lg font-bold text-white">
              {b.amount ? `${b.currency ?? "ZAR"} ${Number(b.amount).toFixed(0)}` : "—"}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default BookingManager;
